/* DynaVap package protection. No jQuery; never clears/rebuilds merchandise. */
(() => {
  "use strict";
  function plan(cart, product, percentage, enabled) {
    const ids = new Set(product.variants.map(v => Number(v.id)));
    const protection = cart.items.filter(i => ids.has(Number(i.variant_id)));
    const merchandise = cart.items.filter(i => !ids.has(Number(i.variant_id)));
    const base = Math.max(0, cart.total_price - protection.reduce((n, i) => n + i.final_line_price, 0));
    const rawTarget = Math.ceil(base * percentage / 100);
    // The merchant-approved cap is the existing "75.00" tier, using its
    // storefront presentment price rather than assuming every currency is USD.
    const cap = product.variants.find(v => Number(v.title) === 75 && v.price > 0);
    const target = cap ? Math.min(rawTarget, cap.price) : rawTarget;
    // "Default" is the legacy penny-priced variant, not a protection tier.
    const tiers = product.variants.filter(v => v.available && v.price > 0 &&
      !/^default(?: title)?$/i.test(v.title)).sort((a, b) => a.price - b.price);
    const variant = target > 0 ? tiers.find(v => v.price >= target && (!cap || v.price <= cap.price)) : null;
    const wanted = enabled && merchandise.length > 0 && variant ? variant : null;
    const keep = wanted ? protection.find(i => Number(i.variant_id) === Number(wanted.id)) : null;
    const updates = {};
    protection.forEach(i => {
      const quantity = i === keep ? 1 : 0;
      if (i.quantity !== quantity) updates[i.key] = quantity;
    });
    return { variant, protection, merchandise, updates, capped: !!cap && rawTarget > cap.price, add: wanted && !keep ? wanted.id : null };
  }
  if (typeof module !== "undefined" && module.exports) module.exports = { plan };
  if (typeof document === "undefined") return;
  if (window.dynaProtectionLoaded) return;
  window.dynaProtectionLoaded = true;

  async function start() {
    const node = document.getElementById("package-protection-config");
    if (!node) return;
    const config = JSON.parse(node.textContent);
    if (!config.enabled) return;
    let tail = Promise.resolve(), queued = 0;
    window.dynaCartRun = operation => {
      queued++;
      document.documentElement.setAttribute("data-cart-busy", "");
      updateWalletState(true);
      const task = tail.then(operation);
      tail = task.catch(() => {});
      return task.finally(() => {
        if (!--queued) {
          document.documentElement.removeAttribute("data-cart-busy");
          updateWalletState(busy() || failed);
        }
      });
    };
    const percentage = Number(config.percentage);
    const root = config.root.replace(/\/?$/, "/");
    let product, lastCart, running = false, pending = false, timer, failed = false, cartLoaded = false;
    window.dynaProtectionCount = cart => cart.items.reduce((count, item) =>
      count + (item.handle === config.handle || (product && product.variants.some(v =>
        Number(v.id) === Number(item.variant_id))) ? 0 : item.quantity), 0);
    let choice = null, message = "", choiceCart = null;
    let checkoutIntent = null, resuming = false;
    const existing = cart => cart.items.filter(i => i.handle === config.handle ||
      (product && product.variants.some(v => Number(v.id) === Number(i.variant_id))));
    const busy = () => running || !!timer || queued > 0;
    function updateWalletState(blocked) {
      document.querySelectorAll("shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons, .dynamic-checkout__content").forEach(el => {
        if (blocked && !el.hasAttribute("inert")) {
          el.setAttribute("data-protection-inert", "");
          el.setAttribute("inert", "");
        } else if (!blocked && el.hasAttribute("data-protection-inert")) {
          el.removeAttribute("inert"); el.removeAttribute("data-protection-inert");
        }
      });
    }
    function resumeCheckout() {
      if (!checkoutIntent || busy() || failed) return;
      const intent = checkoutIntent; checkoutIntent = null;
      if (intent.href) { location.assign(intent.href); return; }
      const form = document.getElementById(intent.formId);
      if (!form) { message = "Please review your cart and click Checkout again."; paint(); return; }
      const button = form.querySelector('[name="checkout"]:not([type="hidden"])') ||
        [...document.querySelectorAll('button[name="checkout"]')].find(b => b.form === form);
      resuming = true;
      try { form.requestSubmit(button || undefined); }
      finally { resuming = false; }
    }
    const choiceKey = "dynavap:protection:choice:v1";
    const cartKey = () => String(lastCart.token || "current-cart").split("?")[0];
    function loadChoice() {
      const key = cartKey();
      if (choiceCart === key) return;
      choiceCart = key; choice = null;
      try {
        const raw = localStorage.getItem(choiceKey);
        let saved = null;
        try { saved = JSON.parse(raw); } catch (_) {}
        if (saved && saved.cart === key && typeof saved.enabled === "boolean") choice = saved.enabled;
        else if (!saved) {
          const modern = localStorage.getItem("protectionRemoved");
          const legacy = localStorage.getItem("removed_protection");
          if (modern === "yes" || legacy === "1") remember(false);
          else if (modern === "no" || legacy === "0") remember(true);
        }
      } catch (_) { /* Storage restrictions must not break the cart. */ }
    }
    function resetChoice() {
      choice = null;
      try {
        [choiceKey, "protectionRemoved", "removed_protection"].forEach(key => localStorage.removeItem(key));
      } catch (_) {}
    }
    const enabled = () => choice === null ? config.defaultEnabled === true : choice;
    function remember(value) {
      choice = value;
      try {
        if (lastCart) localStorage.setItem(choiceKey, JSON.stringify({ cart: cartKey(), enabled: value }));
        else localStorage.removeItem(choiceKey);
        localStorage.setItem("protectionRemoved", choice ? "no" : "yes");
        localStorage.setItem("removed_protection", choice ? "0" : "1");
      } catch (_) {}
    }
    async function request(path, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(root + path, {
        method: body ? "POST" : "GET", credentials: "same-origin", cache: "no-store",
        signal: controller.signal,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...(body ? { body: JSON.stringify(body) } : {})
        });
        if (!response.ok) throw new Error(`Package protection: ${response.status} on ${path}`);
        return await response.json();
      } finally { clearTimeout(timeout); }
    }
    function paint() {
      updateWalletState(busy() || failed);
      if (lastCart) document.querySelectorAll("cart-count").forEach(node => {
        node.textContent = String(window.dynaProtectionCount(lastCart));
      });
      const state = product && lastCart ? plan(lastCart, product, percentage, enabled()) : null;
      const charged = lastCart ? existing(lastCart) : [];
      document.documentElement.toggleAttribute("data-protection-ready", !!(product && lastCart && !failed));
      document.querySelectorAll('[name="checkout"]:not([type="hidden"]), a[href*="/checkout"]').forEach(el => {
        el.setAttribute("aria-busy", String(!!checkoutIntent && busy()));
      });
      document.querySelectorAll("[data-package-protection]").forEach(box => {
        const toggle = box.querySelector("[data-protection-toggle]");
        toggle.disabled = busy() || (!charged.length && (!state || !state.variant));
        toggle.checked = running ? enabled() : charged.length > 0;
        const included = box.querySelector("[data-protection-included]");
        if (included) included.hidden = !charged.length;
        const price = box.querySelector("[data-protection-price]");
        const amount = charged.length ? charged.reduce((sum, i) => sum + i.final_line_price, 0) :
          state && state.variant ? state.variant.price : null;
        price.textContent = amount !== null ? (charged.length ? "charged " : "for ") + new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency", currency: lastCart.currency
        }).format(amount / 100) : "";
        const retry = box.querySelector("[data-protection-retry]");
        if (retry) retry.hidden = !failed;
        const decline = box.querySelector("[data-protection-decline]");
        if (decline) decline.hidden = !failed;
        box.querySelector("[data-protection-status]").textContent = message || (running ? "Updating protection…" : state && !state.variant ?
          "Protection is unavailable for this cart or market. You can still check out." :
          state && state.capped ? "Maximum protection fee for this cart." : "");
      });
    }
    async function renderCart() {
      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true, detail: { cart: lastCart, protection: true }
      }));
      if (config.cartPage) {
        const box = document.querySelector("[data-package-protection]");
        const section = box && box.closest('[id^="shopify-section-"]');
        if (section) {
          const url = new URL(config.cartUrl, location.origin);
          url.searchParams.set("section_id", section.id.replace("shopify-section-", ""));
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          let markup;
          try {
            const response = await fetch(url, { credentials: "same-origin", cache: "no-store", signal: controller.signal });
            if (!response.ok) throw new Error("Cart display refresh failed");
            markup = await response.text();
          } finally { clearTimeout(timeout); }
          const html = new DOMParser().parseFromString(markup, "text/html");
          const replacement = html.getElementById(section.id);
          if (!replacement) throw new Error("Cart section missing");
          const note = section.querySelector('[name="note"]');
          const noteValue = note && note.value;
          section.innerHTML = replacement.innerHTML;
          const newNote = section.querySelector('[name="note"]');
          if (newNote && noteValue !== null) newNote.value = noteValue;
        }
      } else {
        const event = new CustomEvent("cart:refresh", {
          bubbles: true, detail: { protection: true, cart: lastCart }
        });
        document.documentElement.dispatchEvent(event);
        await event.detail.renderPromise;
      }
    }
    async function refresh() {
      if (running) { pending = true; return; }
      running = true; message = ""; paint();
      let changed = false;
      try {
        await window.dynaCartRun(async () => {
        lastCart = await request("cart.js");
        loadChoice();
        if (!enabled()) {
          const updates = Object.fromEntries(existing(lastCart).map(i => [i.key, 0]));
          if (Object.keys(updates).length) {
            lastCart = await request("cart/update.js", { updates }); changed = true;
          }
          if (!product) {
            try { product = await request("products/" + encodeURIComponent(config.handle) + ".js"); }
            catch (_) {
              failed = false;
              message = "Protection removed or not selected. New protection is unavailable; existing fees can still be removed.";
              if (changed) await renderCart();
              return;
            }
          }
        }
        if (!product) product = await request("products/" + encodeURIComponent(config.handle) + ".js");
        if (enabled() && !(percentage > 0 && percentage <= 100)) throw new Error("Invalid protection percentage");
        if (!plan(lastCart, product, percentage, false).merchandise.length) resetChoice();
        if (!cartLoaded && choice === null && lastCart.items.some(i =>
          product.variants.some(v => Number(v.id) === Number(i.variant_id)))) choice = true;
        cartLoaded = true;
        let state = plan(lastCart, product, percentage, enabled());
        if (Object.keys(state.updates).length) {
          lastCart = await request("cart/update.js", { updates: state.updates });
          changed = true;
        }
        state = plan(lastCart, product, percentage, enabled());
        if (state.add) {
          await request("cart/add.js", { items: [{ id: state.add, quantity: 1 }] });
          changed = true;
          lastCart = await request("cart.js");
        }
        failed = false;
        if (changed) await renderCart();
        });
      } catch (error) {
        failed = true;
        checkoutIntent = null;
        message = "Could not update protection. Review the actual fee and removal control, then retry before checkout.";
        console.warn(error);
        // Re-read after a partial mutation so the UI never claims unpaid coverage.
        try { await window.dynaCartRun(async () => {
          lastCart = await request("cart.js"); if (changed) await renderCart();
        }); } catch (_) {}
      } finally {
        running = false; paint();
        if (pending) { pending = false; schedule(); }
        else resumeCheckout();
      }
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(() => { timer = null; refresh(); }, 180); }
    document.addEventListener("change", event => {
      if (!event.target.matches("[data-protection-toggle]")) return;
      remember(event.target.checked);
      refresh();
    });
    ["cart:updated", "cart:refresh", "variant:added", "shopify:section:load"].forEach(name => document.addEventListener(name, event => {
      if (event.detail && event.detail.protection) return;
      schedule();
    }));
    document.addEventListener("cart:rendered", paint);
    window.addEventListener("pageshow", event => { if (event.persisted) schedule(); });
    // Prevent leaving while the customer's coverage selection is being saved.
    document.addEventListener("click", event => {
      if (event.target.closest("[data-protection-retry]")) { refresh(); return; }
      if (event.target.closest("[data-protection-decline]")) { remember(false); refresh(); return; }
      if (event.target.closest("[data-protection-remove]")) remember(false);
      const remove = event.target.closest(".line-item__remove-button");
      const row = remove && remove.closest("[data-protection-variant]");
      if (row && product && product.variants.some(v => Number(v.id) === Number(row.dataset.protectionVariant))) {
        // Persist before native full-cart removal reloads the page.
        remember(false);
      }
      const wallet = event.composedPath().find(el => el instanceof Element && el.matches("shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons, .dynamic-checkout__content"));
      if (wallet && (busy() || failed)) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      const checkout = event.target.closest('[name="checkout"]:not([type="hidden"]), a[href*="/checkout"]');
      if (!resuming && checkout && (busy() || failed)) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (failed) { message = "Retry protection or review/remove the fee before continuing to checkout."; paint(); return; }
        checkoutIntent = checkout.form ? { formId: checkout.form.id } : { href: checkout.href };
        paint();
        if (!running) { clearTimeout(timer); timer = null; refresh(); }
      }
    }, true);
    document.addEventListener("submit", event => {
      const form = event.target;
      const isCheckout = event.submitter?.name === "checkout" ||
        (!event.submitter && form.matches('form[action]') && form.querySelector('[name="checkout"]'));
      if (!resuming && isCheckout && (busy() || failed)) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (failed) { message = "Retry protection or review/remove the fee before continuing to checkout."; paint(); return; }
        checkoutIntent = { formId: form.id };
        paint();
        if (!running) { clearTimeout(timer); timer = null; refresh(); }
      }
    }, true);
    const drawer = document.getElementById("mini-cart");
    if (drawer) new MutationObserver(records => {
      if (records.some(r => r.type === "attributes") && drawer.hasAttribute("open") && !failed) schedule();
      // Native drawer refreshes replace the checkbox; delegation and paint restore it.
      if (records.some(r => r.type === "childList" && [...r.addedNodes].some(n =>
        n.nodeType === 1 && (n.matches("[data-package-protection]") || n.querySelector("[data-package-protection]"))))) paint();
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ["open"] });
    refresh();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();
